/**
 * LES PRÉVISIONS — lot 98, 18/09/2026.
 *
 * Maquette `maquette-analyse-previsions-v2.html`, cadrage « Analyse
 * financière : 3. les prévisions » et « 8. les arbitrages ».
 *
 * Trois onglets :
 *   - Contrats et estimations : la liste, par côté, et le formulaire ;
 *   - Rapprochement : les propositions à confirmer, les lignes passées sans
 *     pièce (décaler ou supprimer), les derniers gestes à défaire ;
 *   - Lignes prévues : 13 semaines d'encaissements et de décaissements, par
 *     activité.
 *
 * LE CONTENEUR APPELLE, LA VUE DESSINE. Aucun calcul ici.
 */

import { useEffect, useMemo, useState } from 'react';
import { ajouterJours, centimes, type LignePrevue, type Prevision } from '@stafa/core';
import type { EchecApi } from './api.js';
import {
  enregistrerPrevision,
  gesteSurLigne,
  lireHistoriquePrevision,
  lirePrevisions,
  supprimerPrevision,
  type EvenementPrevision,
  type GesteLigne,
} from './apiAnalyse.js';
import {
  calculer,
  corpsDe,
  dateFr,
  euros,
  formulaireDe,
  formulaireVide,
  montantVu,
  pourcent,
  reglementEnMots,
  rythmeEnMots,
  semainesPrevues,
  totalRepartition,
  type Cote,
  type Formulaire,
  type PrevisionLue,
  type PrevisionsLues,
} from './previsions.js';

type Issue = Promise<string | null>;

export interface ActionsPrevisions {
  readonly enregistrer: (id: string | null, corps: Record<string, unknown>) => Issue;
  readonly supprimer: (id: string) => Issue;
  readonly geste: (
    previsionId: string,
    date: string,
    geste: GesteLigne,
    corps?: Record<string, unknown>,
  ) => Issue;
  readonly historique: (id: string) => Promise<readonly EvenementPrevision[] | string>;
}

export function EcranPrevisions({
  jeton,
  quandAccesPerdu,
  messageRefus,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
}) {
  const [donnees, setDonnees] = useState<PrevisionsLues | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  function phrase(r: { cause: EchecApi; message?: string }): string {
    if (r.cause === 'acces') quandAccesPerdu();
    return r.message ?? messageRefus(r.cause);
  }

  async function avecJeton<T>(travail: (j: string) => Promise<T>): Promise<T | null> {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return null;
    }
    return travail(j);
  }

  async function charger() {
    const r = await avecJeton(lirePrevisions);
    if (r === null) return;
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else setRefus(phrase(r));
  }

  useEffect(() => {
    void charger();
  }, [jeton]);

  async function puisRecharger(
    r: { etat: 'ok' } | { etat: 'echec'; cause: EchecApi; message?: string } | null,
  ): Issue {
    if (r === null) return 'Session terminée.';
    if (r.etat !== 'ok') return phrase(r);
    await charger();
    return null;
  }

  const actions: ActionsPrevisions = {
    enregistrer: async (id, corps) =>
      puisRecharger(await avecJeton((j) => enregistrerPrevision(j, id, corps))),
    supprimer: async (id) =>
      puisRecharger(await avecJeton((j) => supprimerPrevision(j, id))),
    geste: async (id, date, g, corps) =>
      puisRecharger(await avecJeton((j) => gesteSurLigne(j, id, date, g, corps))),
    historique: async (id) => {
      const r = await avecJeton((j) => lireHistoriquePrevision(j, id));
      if (r === null) return 'Session terminée.';
      return r.etat === 'ok' ? r.valeur : phrase(r);
    },
  };

  return <VuePrevisions donnees={donnees} refus={refus} actions={actions} />;
}

type Onglet = 'recurrences' | 'rapprochement' | 'lignes';

export function VuePrevisions({
  donnees,
  refus,
  actions,
  ongletInitial = 'recurrences',
  ouverteInitiale,
}: {
  readonly donnees: PrevisionsLues | null;
  readonly refus: string | null;
  readonly actions: ActionsPrevisions;
  readonly ongletInitial?: Onglet;
  /** Identifiant de la prévision ouverte, `nouvelle`, ou rien. */
  readonly ouverteInitiale?: string;
}) {
  const [onglet, setOnglet] = useState<Onglet>(ongletInitial);
  const [cote, setCote] = useState<Cote>('client');
  const [ouverte, setOuverte] = useState<string | null>(ouverteInitiale ?? null);
  const [message, setMessage] = useState<{ texte: string; refus: boolean } | null>(
    null,
  );
  const calcul = useMemo(
    () => (donnees === null ? null : calculer(donnees)),
    [donnees],
  );

  if (donnees === null || calcul === null) {
    return (
      <main className="corps corps-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture des prévisions…</p>
        )}
      </main>
    );
  }

  const nbRapprochement =
    calcul.propositions.a_confirmer.length + calcul.propositions.sans_piece.length;
  const nomActivite = new Map(donnees.activites.map((a) => [a.id, a.nom]));
  const prevOuverte =
    ouverte === null || ouverte === 'nouvelle'
      ? null
      : (donnees.previsions.find((p) => p.id === ouverte) ?? null);

  async function faire(travail: () => Issue, reussite: string): Promise<boolean> {
    const issue = await travail();
    setMessage(
      issue === null
        ? { texte: reussite, refus: false }
        : { texte: issue, refus: true },
    );
    return issue === null;
  }

  return (
    <main className="corps corps-analyse">
      <div className="ech-onglets" role="tablist">
        {(
          [
            ['recurrences', 'Contrats et estimations', donnees.previsions.length],
            ['rapprochement', 'Rapprochement', nbRapprochement],
            ['lignes', 'Lignes prévues', calcul.lignes.length],
          ] as const
        ).map(([cle, nom, n]) => (
          <button
            key={cle}
            type="button"
            role="tab"
            aria-selected={onglet === cle}
            className={onglet === cle ? 'ech-onglet actif' : 'ech-onglet'}
            onClick={() => setOnglet(cle)}
          >
            {nom}
            <span
              className={`ech-nb${cle === 'rapprochement' && n > 0 ? ' prev-a-faire' : ''}`}
            >
              {n}
            </span>
          </button>
        ))}
      </div>
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {message !== null && (
        <p
          className={message.refus ? 'bandeau bandeau-refus' : 'bandeau bandeau-fait'}
          role="status"
        >
          {message.texte}
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </p>
      )}

      {onglet === 'recurrences' && (
        <Recurrences
          donnees={donnees}
          cote={cote}
          quandCote={setCote}
          nomActivite={nomActivite}
          quandOuvrir={setOuverte}
        />
      )}
      {onglet === 'rapprochement' && (
        <Rapprochement
          donnees={donnees}
          calcul={calcul}
          faire={faire}
          actions={actions}
        />
      )}
      {onglet === 'lignes' && <Lignes donnees={donnees} calcul={calcul} />}

      {ouverte !== null && (
        <PanneauPrevision
          key={ouverte}
          prevision={prevOuverte}
          cote={prevOuverte?.cote ?? cote}
          donnees={donnees}
          actions={actions}
          quandFermer={() => setOuverte(null)}
          quandFait={(texte) => {
            setOuverte(null);
            setMessage({ texte, refus: false });
          }}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Onglet 1 — contrats et estimations
// ---------------------------------------------------------------------------

function repartitionEnMots(p: PrevisionLue, noms: ReadonlyMap<string, string>): string {
  return p.parts
    .map((x) => {
      const nom = x.centre_id === null ? 'Structure' : (noms.get(x.centre_id) ?? '?');
      const v =
        p.repartition === '%'
          ? `${Number(x.valeur).toLocaleString('fr-FR')} %`
          : euros(centimes(x.valeur), false);
      return p.parts.length === 1 && p.repartition === '%' ? nom : `${nom} ${v}`;
    })
    .join(' · ');
}

function Recurrences({
  donnees,
  cote,
  quandCote,
  nomActivite,
  quandOuvrir,
}: {
  readonly donnees: PrevisionsLues;
  readonly cote: Cote;
  readonly quandCote: (c: Cote) => void;
  readonly nomActivite: ReadonlyMap<string, string>;
  readonly quandOuvrir: (id: string) => void;
}) {
  const liste = donnees.previsions.filter((p) => p.cote === cote);
  return (
    <section className="ech-carte">
      <div className="prev-entete">
        <div className="prev-cotes" role="tablist">
          {(['client', 'fournisseur'] as const).map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={cote === c}
              className={cote === c ? 'prev-cote actif' : 'prev-cote'}
              onClick={() => quandCote(c)}
            >
              {c === 'client' ? 'Clients' : 'Fournisseurs et charges'}{' '}
              <span className="ech-nb">
                {donnees.previsions.filter((p) => p.cote === c).length}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="bouton bouton-petit"
          onClick={() => quandOuvrir('nouvelle')}
        >
          {cote === 'client'
            ? 'Nouveau contrat ou estimation client'
            : 'Nouvelle charge'}
        </button>
      </div>
      <p className="ech-sous">
        {cote === 'client'
          ? 'Contrats récurrents et estimations : chacun produit des préfactures prévues, jamais envoyées, qui disparaissent quand la vraie facture arrive.'
          : 'Contrats et estimations de charges : loyers, carburant, salaires… Une charge sans fournisseur Pennylane sort des prévisions une fois sa date passée.'}
      </p>
      {liste.length === 0 ? (
        <p className="ech-vide">Aucune prévision de ce côté.</p>
      ) : (
        <div className="ech-tableau">
          <table>
            <thead>
              <tr>
                <th>{cote === 'client' ? 'Client' : 'Fournisseur ou charge'}</th>
                <th>Type</th>
                <th className="nombre">Montant</th>
                <th>Rythme</th>
                <th>Règlement</th>
                <th>Répartition</th>
                <th>Période</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((p) => (
                <tr key={p.id} className={p.actif ? undefined : 'prev-inactive'}>
                  <td className="ech-tiers">
                    <button
                      type="button"
                      className="ech-numero"
                      onClick={() => quandOuvrir(p.id)}
                    >
                      {p.tiers_nom}
                    </button>
                    {p.libelle !== null && (
                      <span className="prev-libelle">{p.libelle}</span>
                    )}
                    {p.tiers_pennylane_id === null && (
                      <span className="ech-etiquette">sans pièce Pennylane</span>
                    )}
                    {!p.actif && <span className="ech-etiquette alerte">inactif</span>}
                  </td>
                  <td data-etiquette="Type">
                    <span
                      className={`ech-etiquette${p.type === 'estimation' ? ' prev-estimation' : ''}`}
                    >
                      {p.type}
                    </span>
                  </td>
                  <td className="nombre ech-montant" data-etiquette="Montant">
                    {euros(centimes(p.montant_ht))} HT
                    <span className="ech-doux ech-petit prev-bloc">
                      {euros(
                        Math.round(
                          centimes(p.montant_ht) * (1 + Number(p.taux_tva) / 100),
                        ),
                      )}{' '}
                      TTC
                    </span>
                  </td>
                  <td data-etiquette="Rythme">{rythmeEnMots(p)}</td>
                  <td className="ech-petit" data-etiquette="Règlement">
                    {reglementEnMots(
                      p,
                      p.tiers_pennylane_id === null
                        ? null
                        : (donnees.retards[p.tiers_pennylane_id] ?? null),
                    )}
                  </td>
                  <td className="ech-petit" data-etiquette="Répartition">
                    {repartitionEnMots(p, nomActivite)}
                  </td>
                  <td className="ech-petit" data-etiquette="Période">
                    {p.rythme === 'une_fois'
                      ? dateFr(p.du)
                      : `du ${dateFr(p.du)}${p.au === null ? '' : ` au ${dateFr(p.au)}`}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Onglet 2 — rapprochement
// ---------------------------------------------------------------------------

function Rapprochement({
  donnees,
  calcul,
  faire,
  actions,
}: {
  readonly donnees: PrevisionsLues;
  readonly calcul: ReturnType<typeof calculer>;
  readonly faire: (t: () => Issue, reussite: string) => Promise<boolean>;
  readonly actions: ActionsPrevisions;
}) {
  const [decalages, setDecalages] = useState<Record<string, string>>({});
  const [choix, setChoix] = useState<Record<string, string>>({});
  const [enCours, setEnCours] = useState(false);
  const nom = (id: string) => donnees.previsions.find((p) => p.id === id);
  const { a_confirmer, sans_piece } = calcul.propositions;

  async function lancer(t: () => Issue, reussite: string) {
    setEnCours(true);
    await faire(t, reussite);
    setEnCours(false);
  }

  const supprimees = donnees.ecarts.filter((e) => e.supprimee);

  return (
    <>
      <section className="ech-carte">
        <div className="prev-entete">
          <div>
            <h2 className="prev-titre">À confirmer ({a_confirmer.length})</h2>
            <p className="ech-sous">
              Stafa propose la pièce la plus proche du même tiers. Tolérance : ±5 % pour
              un contrat, ±30 % pour une estimation ; au-delà, la proposition reste, en
              rouge.
            </p>
          </div>
          {a_confirmer.length > 1 && (
            <button
              type="button"
              className="bouton bouton-petit"
              disabled={enCours}
              onClick={() =>
                void lancer(
                  async () => {
                    for (const x of a_confirmer) {
                      const issue = await actions.geste(
                        x.ligne.prevision_id,
                        x.ligne.date_rythme,
                        'rapprocher',
                        {
                          pennylane_id:
                            choix[x.ligne.cle] ?? x.meilleur.piece.pennylane_id,
                        },
                      );
                      if (issue !== null) return issue;
                    }
                    return null;
                  },
                  `${String(a_confirmer.length)} rapprochement(s) confirmé(s).`,
                )
              }
            >
              Tout confirmer
            </button>
          )}
        </div>
        {a_confirmer.length === 0 ? (
          <p className="ech-vide">Rien à confirmer.</p>
        ) : (
          <ul className="prev-propositions">
            {a_confirmer.map((x) => {
              const p = nom(x.ligne.prevision_id);
              const choisie = choix[x.ligne.cle] ?? x.meilleur.piece.pennylane_id;
              const c =
                [x.meilleur, ...x.autres].find(
                  (a) => a.piece.pennylane_id === choisie,
                ) ?? x.meilleur;
              return (
                <li key={x.ligne.cle} className="prev-proposition">
                  <div>
                    <strong>{p?.tiers_nom}</strong>
                    <span className="ech-doux"> · {p?.libelle ?? p?.type}</span>
                    <span className="prev-bloc ech-petit">
                      Prévu le {dateFr(x.ligne.date)} : {euros(x.ligne.ttc_centimes)}{' '}
                      TTC
                    </span>
                  </div>
                  <div>
                    {x.autres.length === 0 ? (
                      <strong>{c.piece.numero ?? `n° ${c.piece.pennylane_id}`}</strong>
                    ) : (
                      <select
                        aria-label="Pièce proposée"
                        value={choisie}
                        onChange={(e) =>
                          setChoix({ ...choix, [x.ligne.cle]: e.target.value })
                        }
                      >
                        {[x.meilleur, ...x.autres].map((a) => (
                          <option
                            key={a.piece.pennylane_id}
                            value={a.piece.pennylane_id}
                          >
                            {a.piece.numero ?? a.piece.pennylane_id} ·{' '}
                            {dateFr(a.piece.date)} · {euros(a.piece.ttc_centimes)}
                          </option>
                        ))}
                      </select>
                    )}
                    <span className="prev-bloc ech-petit">
                      Reçue le {dateFr(c.piece.date)} : {euros(c.piece.ttc_centimes)}{' '}
                      TTC ·{' '}
                      <span className={c.dans_tolerance ? 'ech-ok' : 'ech-ko'}>
                        écart {c.ecart_centimes > 0 ? '+' : ''}
                        {euros(c.ecart_centimes)} ({pourcent(c.relatif)})
                      </span>
                    </span>
                  </div>
                  <div className="ech-actions">
                    <button
                      type="button"
                      className="bouton bouton-petit"
                      disabled={enCours}
                      onClick={() =>
                        void lancer(
                          () =>
                            actions.geste(
                              x.ligne.prevision_id,
                              x.ligne.date_rythme,
                              'rapprocher',
                              {
                                pennylane_id: choisie,
                              },
                            ),
                          'Rapprochement confirmé.',
                        )
                      }
                    >
                      Confirmer
                    </button>
                    <button
                      type="button"
                      className="bouton bouton-discret bouton-petit"
                      disabled={enCours}
                      onClick={() =>
                        void lancer(
                          () =>
                            actions.geste(
                              x.ligne.prevision_id,
                              x.ligne.date_rythme,
                              'refuser',
                              {
                                pennylane_id: choisie,
                              },
                            ),
                          'Proposition écartée : elle ne reviendra pas pour cette ligne.',
                        )
                      }
                    >
                      Ce n’est pas elle
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="ech-carte">
        <h2 className="prev-titre">Lignes passées sans pièce ({sans_piece.length})</h2>
        <p className="ech-sous">
          La date prévue est dépassée depuis 3 jours et aucune pièce ne correspond :
          décalez-la, ou supprimez-la si elle ne viendra pas.
        </p>
        {sans_piece.length === 0 ? (
          <p className="ech-vide">Aucune.</p>
        ) : (
          <ul className="prev-propositions">
            {sans_piece.map((l) => {
              const p = nom(l.prevision_id);
              const nouvelle = decalages[l.cle] ?? ajouterJours(donnees.aujourdhui, 7);
              return (
                <li key={l.cle} className="prev-proposition">
                  <div>
                    <strong>{p?.tiers_nom}</strong>
                    <span className="ech-doux"> · {p?.libelle ?? p?.type}</span>
                    <span className="prev-bloc ech-petit">
                      Prévue le {dateFr(l.date)}
                      {l.decalee && ' (déjà décalée)'} : {euros(l.ttc_centimes)} TTC
                    </span>
                  </div>
                  <div className="ech-actions">
                    <label className="ech-champ">
                      Nouvelle date
                      <input
                        type="date"
                        value={nouvelle}
                        onChange={(e) =>
                          setDecalages({ ...decalages, [l.cle]: e.target.value })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="bouton bouton-petit"
                      disabled={enCours || nouvelle === ''}
                      onClick={() =>
                        void lancer(
                          () =>
                            actions.geste(l.prevision_id, l.date_rythme, 'decaler', {
                              au: nouvelle,
                            }),
                          `Ligne décalée au ${dateFr(nouvelle)}.`,
                        )
                      }
                    >
                      Décaler
                    </button>
                    <button
                      type="button"
                      className="bouton bouton-discret bouton-petit"
                      disabled={enCours}
                      onClick={() =>
                        void lancer(
                          () =>
                            actions.geste(l.prevision_id, l.date_rythme, 'supprimer'),
                          'Ligne supprimée. « Défaire » la remet, plus bas.',
                        )
                      }
                    >
                      Supprimer
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="ech-carte">
        <h2 className="prev-titre">Derniers gestes</h2>
        {donnees.rapprochements.length === 0 && supprimees.length === 0 ? (
          <p className="ech-vide">Aucun.</p>
        ) : (
          <ul className="ech-liste">
            {donnees.rapprochements.slice(0, 15).map((r) => {
              const p = nom(r.prevision_id);
              const ecart = centimes(r.montant_reel) - centimes(r.montant_prevu);
              return (
                <li key={`r${r.prevision_id}${r.date_rythme}`}>
                  <span>
                    <strong>{p?.tiers_nom ?? 'Prévision supprimée'}</strong> ·{' '}
                    {dateFr(r.date_rythme)} ↔ {r.numero ?? r.pennylane_id}
                  </span>
                  <span className={Math.abs(ecart) === 0 ? 'ech-doux' : 'ech-ko'}>
                    prévu {euros(centimes(r.montant_prevu))}, reçu{' '}
                    {euros(centimes(r.montant_reel))}
                  </span>
                  <span className="ech-doux ech-petit">{r.par}</span>
                  <button
                    type="button"
                    className="ech-lien"
                    disabled={enCours}
                    onClick={() =>
                      void lancer(
                        () => actions.geste(r.prevision_id, r.date_rythme, 'defaire'),
                        'Rapprochement défait : la ligne redevient prévue.',
                      )
                    }
                  >
                    Défaire
                  </button>
                </li>
              );
            })}
            {supprimees.map((e) => (
              <li key={`s${e.prevision_id}${e.date_rythme}`}>
                <span>
                  <strong>{nom(e.prevision_id)?.tiers_nom}</strong> · ligne du{' '}
                  {dateFr(e.date_rythme)} supprimée
                </span>
                <span className="ech-doux ech-petit">{e.par}</span>
                <button
                  type="button"
                  className="ech-lien"
                  disabled={enCours}
                  onClick={() =>
                    void lancer(
                      () => actions.geste(e.prevision_id, e.date_rythme, 'retablir'),
                      'Ligne remise dans les prévisions.',
                    )
                  }
                >
                  Défaire
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Onglet 3 — lignes prévues
// ---------------------------------------------------------------------------

function Lignes({
  donnees,
  calcul,
}: {
  readonly donnees: PrevisionsLues;
  readonly calcul: ReturnType<typeof calculer>;
}) {
  const [activite, setActivite] = useState('tout');
  const { semaines, au_dela } = semainesPrevues(calcul, donnees.aujourdhui, activite);
  const enc = semaines.reduce((s, x) => s + x.encaissements, 0);
  const dec = semaines.reduce((s, x) => s + x.decaissements, 0);
  const max = Math.max(
    1,
    ...semaines.flatMap((s) => [s.encaissements, s.decaissements]),
  );
  const visibles = calcul.lignes
    .map((l) => ({ l, p: calcul.previsions.get(l.prevision_id) as Prevision }))
    .filter(({ l, p }) => p !== undefined && montantVu(l, p, activite) !== 0);
  const premiere = semaines[0]?.numero ?? 0;
  const derniere = semaines[semaines.length - 1]?.numero ?? 0;
  const lue = (id: string) => donnees.previsions.find((p) => p.id === id);

  return (
    <>
      <div className="prev-filtre">
        <label className="ech-champ">
          Activité
          <select value={activite} onChange={(e) => setActivite(e.target.value)}>
            <option value="tout">Vue d’ensemble</option>
            {donnees.activites.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nom}
              </option>
            ))}
            <option value="">Structure</option>
          </select>
        </label>
      </div>
      <div className="ech-tuiles">
        <div className="ech-tuile">
          <span className="ech-tuile-titre">
            Encaissements prévus S{premiere} → S{derniere}
          </span>
          <span className="ech-tuile-valeur prev-enc">{euros(enc, false)}</span>
          <span className="ech-tuile-sous">contrats et estimations clients</span>
        </div>
        <div className="ech-tuile">
          <span className="ech-tuile-titre">
            Décaissements prévus S{premiere} → S{derniere}
          </span>
          <span className="ech-tuile-valeur prev-dec">{euros(dec, false)}</span>
          <span className="ech-tuile-sous">contrats et estimations de charges</span>
        </div>
        <div className="ech-tuile">
          <span className="ech-tuile-titre">Écart des prévisions</span>
          <span
            className={`ech-tuile-valeur ${enc - dec < 0 ? 'prev-dec' : 'prev-enc'}`}
          >
            {euros(enc - dec, false)}
          </span>
          <span className="ech-tuile-sous">hors factures déjà émises ou reçues</span>
        </div>
        <div className="ech-tuile">
          <span className="ech-tuile-titre">Au-delà de S{derniere}</span>
          <span className="ech-tuile-valeur">
            {euros(au_dela.enc - au_dela.dec, false)}
          </span>
          <span className="ech-tuile-sous">
            {euros(au_dela.enc, false)} entrées · {euros(au_dela.dec, false)} sorties
          </span>
        </div>
      </div>

      <section className="ech-carte">
        <h2 className="prev-titre">Semaine par semaine, à la date de règlement</h2>
        <div
          className="prev-barres"
          role="img"
          aria-label="Encaissements et décaissements prévus par semaine"
        >
          {semaines.map((s) => (
            <div
              key={s.lundi}
              className="prev-semaine"
              title={`S${String(s.numero)} : +${euros(s.encaissements)} / −${euros(s.decaissements)}`}
            >
              <div className="prev-paire">
                <span
                  className="prev-barre prev-barre-enc"
                  style={{ height: `${String((s.encaissements / max) * 100)}%` }}
                />
                <span
                  className="prev-barre prev-barre-dec"
                  style={{ height: `${String((s.decaissements / max) * 100)}%` }}
                />
              </div>
              <span className="prev-axe">S{s.numero}</span>
            </div>
          ))}
        </div>
        <p className="ech-legende">
          <span>
            <span className="ech-cle prev-cle-enc" /> encaissements
          </span>
          <span>
            <span className="ech-cle prev-cle-dec" /> décaissements
          </span>
        </p>
      </section>

      <section className="ech-carte">
        <div className="ech-tableau">
          <table>
            <thead>
              <tr>
                <th>Pièce attendue</th>
                <th>Tiers</th>
                <th>Règlement prévu</th>
                <th className="nombre">Montant TTC</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(({ l, p }) => (
                <LigneVue
                  key={l.cle}
                  l={l}
                  montant={montantVu(l, p, activite)}
                  lue={lue(l.prevision_id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function LigneVue({
  l,
  montant,
  lue,
}: {
  readonly l: LignePrevue;
  readonly montant: number;
  readonly lue: PrevisionLue | undefined;
}) {
  return (
    <tr className={l.passee ? 'ech-a-revoir' : undefined}>
      <td>
        {dateFr(l.date)}
        <span className="ech-etiquettes">
          {lue?.type === 'estimation' && (
            <span className="ech-etiquette prev-estimation">estimation</span>
          )}
          {lue?.prelevement === true && (
            <span className="ech-etiquette">prélèvement</span>
          )}
          {l.decalee && <span className="ech-etiquette">décalée</span>}
          {l.passee && <span className="ech-etiquette alerte">sans pièce</span>}
        </span>
      </td>
      <td className="ech-tiers">
        {lue?.tiers_nom}
        {lue?.libelle !== null && lue?.libelle !== undefined && (
          <span className="prev-libelle">{lue.libelle}</span>
        )}
      </td>
      <td data-etiquette="Règlement">{dateFr(l.reglement)}</td>
      <td
        className={`nombre ech-montant ${lue?.cote === 'client' ? 'prev-enc' : 'prev-dec'}`}
      >
        {lue?.cote === 'client' ? '+' : '−'}
        {euros(montant)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Le formulaire
// ---------------------------------------------------------------------------

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MOIS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];
const AUTRE = '__autre__';

function PanneauPrevision({
  prevision,
  cote,
  donnees,
  actions,
  quandFermer,
  quandFait,
}: {
  readonly prevision: PrevisionLue | null;
  readonly cote: Cote;
  readonly donnees: PrevisionsLues;
  readonly actions: ActionsPrevisions;
  readonly quandFermer: () => void;
  readonly quandFait: (texte: string) => void;
}) {
  const [f, setF] = useState<Formulaire>(
    prevision === null
      ? formulaireVide(cote, donnees.aujourdhui)
      : formulaireDe(prevision),
  );
  const [autre, setAutre] = useState(
    prevision !== null && prevision.tiers_pennylane_id === null,
  );
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [historique, setHistorique] = useState<
    readonly EvenementPrevision[] | string | null
  >(null);
  const [confirmer, setConfirmer] = useState(false);
  const tiers = donnees.tiers[cote];
  const { total, cible } = totalRepartition(f);
  const mensuel = f.rythme === 'mensuel' || f.rythme === 'trimestriel';

  useEffect(() => {
    if (prevision !== null) void actions.historique(prevision.id).then(setHistorique);
  }, [prevision?.id]);

  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [quandFermer]);

  const maj = (x: Partial<Formulaire>) => setF({ ...f, ...x });

  async function enregistrer() {
    setEnvoi(true);
    setRefus(null);
    const issue = await actions.enregistrer(prevision?.id ?? null, corpsDe(f, cote));
    setEnvoi(false);
    if (issue !== null) setRefus(issue);
    else quandFait(prevision === null ? 'Prévision créée.' : 'Prévision enregistrée.');
  }

  const ecartRepartition = cible - total;
  const ttc = Math.round(
    centimes(f.montant_ht.replace(',', '.').replace(/\s/g, '')) *
      (1 + Number(f.taux_tva.replace(',', '.') || '0') / 100),
  );

  return (
    <div className="ech-voile" role="presentation" onClick={quandFermer}>
      <div
        className="ech-panneau"
        role="dialog"
        aria-modal="true"
        aria-label={prevision === null ? 'Nouvelle prévision' : prevision.tiers_nom}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>{prevision === null ? 'Nouvelle prévision' : prevision.tiers_nom}</h2>
            <p className="ech-sous">
              {cote === 'client' ? 'Côté clients' : 'Côté fournisseurs et charges'}
              {prevision !== null && ` · créée par ${prevision.cree_par}`}
            </p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>

        <div className="prev-grille">
          <label className="ech-champ">
            Type
            <select
              value={f.type}
              onChange={(e) => maj({ type: e.target.value as Formulaire['type'] })}
            >
              <option value="contrat">Contrat (tolérance 5 %)</option>
              <option value="estimation">Estimation (tolérance 30 %)</option>
            </select>
          </label>
          <label className="ech-champ">
            {cote === 'client' ? 'Client' : 'Fournisseur'}
            <select
              value={autre ? AUTRE : f.tiers_pennylane_id}
              onChange={(e) => {
                if (e.target.value === AUTRE) {
                  setAutre(true);
                  maj({ tiers_pennylane_id: '', tiers_nom: '' });
                } else {
                  setAutre(false);
                  const t = tiers.find((x) => x.id === e.target.value);
                  maj({ tiers_pennylane_id: e.target.value, tiers_nom: t?.nom ?? '' });
                }
              }}
            >
              <option value="">— choisir —</option>
              {tiers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nom}
                </option>
              ))}
              <option value={AUTRE}>
                {cote === 'client'
                  ? 'Autre (sans client Pennylane)'
                  : 'Autre charge (salaires, impôts…)'}
              </option>
            </select>
          </label>
          {autre && (
            <label className="ech-champ">
              Nom
              <input
                type="text"
                maxLength={120}
                value={f.tiers_nom}
                onChange={(e) => maj({ tiers_nom: e.target.value })}
              />
            </label>
          )}
          <label className="ech-champ prev-large">
            Libellé
            <input
              type="text"
              maxLength={200}
              value={f.libelle}
              onChange={(e) => maj({ libelle: e.target.value })}
            />
          </label>
          {cote === 'fournisseur' && (
            <label className="ech-champ">
              Famille
              <select
                value={f.famille}
                onChange={(e) =>
                  maj({ famille: e.target.value as Formulaire['famille'] })
                }
              >
                <option value="exploitation">Exploitation</option>
                <option value="structure">Structure</option>
              </select>
            </label>
          )}
          <label className="ech-champ">
            Montant HT (€)
            <input
              type="text"
              inputMode="decimal"
              value={f.montant_ht}
              onChange={(e) => maj({ montant_ht: e.target.value })}
            />
          </label>
          <label className="ech-champ">
            TVA (%)
            <input
              type="text"
              inputMode="decimal"
              value={f.taux_tva}
              onChange={(e) => maj({ taux_tva: e.target.value })}
            />
          </label>
          <p className="prev-ttc">
            = <strong>{euros(ttc)}</strong> TTC
          </p>

          <label className="ech-champ">
            Rythme
            <select
              value={f.rythme}
              onChange={(e) => maj({ rythme: e.target.value as Formulaire['rythme'] })}
            >
              <option value="hebdo">Chaque semaine</option>
              <option value="mensuel">Chaque mois</option>
              <option value="trimestriel">Tous les 3 mois</option>
              <option value="une_fois">Une seule fois</option>
            </select>
          </label>
          {f.rythme === 'hebdo' && (
            <label className="ech-champ">
              Jour
              <select
                value={f.jour_semaine}
                onChange={(e) => maj({ jour_semaine: e.target.value })}
              >
                {JOURS.map((j, i) => (
                  <option key={j} value={String(i)}>
                    {j}
                  </option>
                ))}
              </select>
            </label>
          )}
          {mensuel && (
            <label className="ech-champ">
              Jour du mois
              <select
                value={f.jour_mois}
                onChange={(e) => maj({ jour_mois: e.target.value })}
              >
                {Array.from({ length: 31 }, (_, i) => (
                  <option key={i} value={String(i + 1)}>
                    le {i + 1}
                  </option>
                ))}
                <option value="fin">en fin de mois</option>
              </select>
            </label>
          )}
          {f.rythme === 'trimestriel' && (
            <label className="ech-champ">
              À partir de
              <select
                value={f.mois_depart}
                onChange={(e) => maj({ mois_depart: e.target.value })}
              >
                {MOIS.map((m, i) => (
                  <option key={m} value={String(i + 1)}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="ech-champ">
            {f.rythme === 'une_fois' ? 'Date' : 'Du'}
            <input
              type="date"
              value={f.du}
              onChange={(e) => maj({ du: e.target.value })}
            />
          </label>
          {f.rythme !== 'une_fois' && (
            <label className="ech-champ">
              Au (facultatif)
              <input
                type="date"
                value={f.au}
                onChange={(e) => maj({ au: e.target.value })}
              />
            </label>
          )}
          <label className="ech-champ">
            {cote === 'client' ? 'Échéance à (jours)' : 'Payé à (jours)'}
            <input
              type="number"
              min={0}
              max={365}
              value={f.delai_jours}
              onChange={(e) => maj({ delai_jours: e.target.value })}
            />
          </label>
          {cote === 'fournisseur' ? (
            <label className="ech-bascule prev-large">
              <input
                type="checkbox"
                checked={f.prelevement}
                onChange={(e) => maj({ prelevement: e.target.checked })}
              />
              Prélèvement
            </label>
          ) : (
            <label className="ech-bascule prev-large">
              <input
                type="checkbox"
                checked={f.remplace_transports}
                onChange={(e) => maj({ remplace_transports: e.target.checked })}
              />
              Remplace les prévisions tirées des transports planifiés de ce client
            </label>
          )}
          <label className="ech-bascule prev-large">
            <input
              type="checkbox"
              checked={f.actif}
              onChange={(e) => maj({ actif: e.target.checked })}
            />
            Active
          </label>
        </div>

        <section className="ech-bloc">
          <h3>Répartition par activité</h3>
          <div
            className="prev-modes"
            role="radiogroup"
            aria-label="Mode de répartition"
          >
            {(['%', '€'] as const).map((m) => (
              <label key={m} className="ech-bascule">
                <input
                  type="radio"
                  name="mode"
                  checked={f.repartition === m}
                  onChange={() => maj({ repartition: m })}
                />
                en {m}
              </label>
            ))}
          </div>
          <table className="ech-tranches">
            <tbody>
              {f.parts.map((x, i) => (
                <tr key={i}>
                  <td>
                    <select
                      aria-label={`Activité ${String(i + 1)}`}
                      value={x.centre_id}
                      onChange={(e) =>
                        maj({
                          parts: f.parts.map((y, k) =>
                            k === i ? { ...y, centre_id: e.target.value } : y,
                          ),
                        })
                      }
                    >
                      <option value="">Structure</option>
                      {donnees.activites.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nom}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Part ${String(i + 1)}`}
                      value={x.valeur}
                      onChange={(e) =>
                        maj({
                          parts: f.parts.map((y, k) =>
                            k === i ? { ...y, valeur: e.target.value } : y,
                          ),
                        })
                      }
                    />{' '}
                    {f.repartition}
                  </td>
                  <td>
                    {f.parts.length > 1 && (
                      <button
                        type="button"
                        className="ech-lien"
                        onClick={() =>
                          maj({ parts: f.parts.filter((_, k) => k !== i) })
                        }
                      >
                        retirer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ech-actions">
            <button
              type="button"
              className="bouton bouton-discret bouton-petit"
              onClick={() =>
                maj({ parts: [...f.parts, { centre_id: '', valeur: '' }] })
              }
            >
              Ajouter une activité
            </button>
            <span className={ecartRepartition === 0 ? 'ech-ok' : 'ech-ko'}>
              {ecartRepartition === 0
                ? 'Le total tombe juste.'
                : f.repartition === '%'
                  ? `Total : ${(total / 100).toLocaleString('fr-FR')} % sur 100 %.`
                  : `Total : ${euros(total)} sur ${euros(cible)} HT (${ecartRepartition > 0 ? 'il manque' : 'de trop :'} ${euros(Math.abs(ecartRepartition))}).`}
            </span>
          </div>
        </section>

        {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
        <div className="ech-actions">
          <button
            type="button"
            className="bouton"
            disabled={
              envoi ||
              f.tiers_nom.trim() === '' ||
              (!autre && f.tiers_pennylane_id === '')
            }
            onClick={() => void enregistrer()}
          >
            Enregistrer
          </button>
          <button type="button" className="bouton bouton-discret" onClick={quandFermer}>
            Annuler
          </button>
          {prevision !== null &&
            (confirmer ? (
              <>
                <span className="ech-ko">
                  Supprimer cette prévision et ses lignes ?
                </span>
                <button
                  type="button"
                  className="bouton bouton-petit prev-danger"
                  disabled={envoi}
                  onClick={() => {
                    setEnvoi(true);
                    void actions.supprimer(prevision.id).then((issue) => {
                      setEnvoi(false);
                      if (issue !== null) setRefus(issue);
                      else quandFait('Prévision supprimée. Son historique est gardé.');
                    });
                  }}
                >
                  Oui, supprimer
                </button>
                <button
                  type="button"
                  className="ech-lien"
                  onClick={() => setConfirmer(false)}
                >
                  non
                </button>
              </>
            ) : (
              <button
                type="button"
                className="ech-lien prev-a-droite"
                onClick={() => setConfirmer(true)}
              >
                Supprimer la prévision
              </button>
            ))}
        </div>

        {prevision !== null && (
          <section className="ech-bloc">
            <h3>Historique</h3>
            {historique === null ? (
              <p className="attente">Lecture…</p>
            ) : typeof historique === 'string' ? (
              <p className="bandeau bandeau-refus">{historique}</p>
            ) : (
              <ul className="ech-historique">
                {historique.map((h, i) => (
                  <li key={i}>
                    <span className="ech-doux ech-petit">
                      {dateFr(h.le.slice(0, 10))} · {h.par}
                    </span>
                    <span>{evenementEnMots(h)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

const ACTIONS: Readonly<Record<string, string>> = {
  creee: 'Prévision créée',
  modifiee: 'Prévision modifiée',
  supprimee_prevision: 'Prévision supprimée',
  decalee: 'Ligne décalée',
  ligne_supprimee: 'Ligne supprimée',
  retablie: 'Ligne rétablie',
  rapprochee: 'Ligne rapprochée',
  rapprochement_defait: 'Rapprochement défait',
  proposition_refusee: 'Proposition écartée',
};

function evenementEnMots(h: EvenementPrevision): string {
  const base = ACTIONS[h.action] ?? h.action;
  const ligne = h.date_rythme === null ? '' : ` du ${dateFr(h.date_rythme)}`;
  const d = h.details;
  if (h.action === 'decalee' && typeof d.au === 'string')
    return `${base}${ligne} au ${dateFr(d.au)}`;
  if (h.action === 'rapprochee') {
    return `${base}${ligne} : ${String(d.piece ?? '')} (prévu ${euros(centimes(String(d.prevu ?? '0')))}, reçu ${euros(centimes(String(d.reel ?? '0')))})`;
  }
  if (h.action === 'modifiee') {
    const champs = Object.keys(d);
    return champs.length === 0
      ? `${base} (répartition)`
      : `${base} : ${champs.join(', ')}`;
  }
  if (typeof d.piece === 'string') return `${base}${ligne} : ${d.piece}`;
  return `${base}${ligne}`;
}
